import { useEffect, useState } from 'react';
import { fetchAuthBlobUrl } from '@utils/blobUrl';
import { isTrustedApiUrl } from '@utils/apiOrigin';

/**
 * Fetch an auth-protected image URL and return a blob URL for display.
 *
 * Standard `<img>` tags cannot attach Bearer tokens, so the image is fetched
 * via the authenticated API client and wrapped in an object URL.
 *
 * `hasError` is true when the fetch has failed, allowing consumers to
 * stop showing loading indicators instead of spinning forever.
 */
const useAuthImage = (imageUrl: string | undefined): { url: string | null; hasError: boolean } => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!imageUrl || !isTrustedApiUrl(imageUrl)) return;

    let revoked = false;
    let objectUrl: string | null = null;

    fetchAuthBlobUrl(imageUrl)
      .then((url) => {
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!revoked) setHasError(true);
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBlobUrl(null);
      setHasError(false);
    };
  }, [imageUrl]);

  return { url: blobUrl, hasError };
};

export default useAuthImage;
